import { forwardRef, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import { ArrowIcon } from '~/assets/icons';
import styles from './DateTimePicker.module.scss';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

interface DateTimePickerProps {
  placeholder?: string;
  value: Date | null;
  onChange?: (date: Date | null) => void;
  errorMessage?: string;
  disabled?: boolean;
}

const DateTimePicker = ({ placeholder, value, onChange, errorMessage, disabled = false }: DateTimePickerProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dateText, setDateText] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const date = dayjs(value).toDate();
      setSelectedDate(date);
      setDateText(dayjs(date).format('MM/DD/YYYY - hh:mmA'));
    } else {
      setSelectedDate(null);
      setDateText('');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleChange = (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      setDateText(dayjs(date).format('MM/DD/YYYY - hh:mmA'));
    } else {
      setDateText('');
    }
    if (onChange) {
      onChange(date);
    }
    setIsOpen(false);
  };

  const CustomInput = forwardRef<HTMLInputElement, { value?: string; onClick?: () => void }>(
    ({ value, onClick }, ref) => (
      <div className={cx('date-picker-wrapper')}>
        <input
          ref={ref}
          type="text"
          value={value || ''}
          onClick={() => {
            if (!disabled) {
              setIsOpen(true);
              if (onClick) onClick();
            }
          }}
          readOnly
          placeholder={placeholder}
          className={cx('date-input', { error: !!errorMessage, disabled })}
          disabled={disabled}
        />
        <ArrowIcon className={cx('arrow-icon', { rotated: isOpen })} />
      </div>
    )
  );

  CustomInput.displayName = 'CustomInput';

  return (
    <div className={cx('date-time-picker-container')} ref={pickerRef}>
      <DatePicker
        selected={selectedDate}
        onChange={handleChange}
        showTimeSelect
        timeFormat="hh:mm aa"
        timeIntervals={15}
        dateFormat="MM/dd/yyyy hh:mm aa"
        customInput={<CustomInput value={dateText} />}
        onClickOutside={() => setIsOpen(false)}
        popperPlacement="bottom-start"
        showPopperArrow={false}
        calendarStartDay={1}
        disabled={disabled}
        open={isOpen && !disabled}
      />
      {errorMessage && <p className={cx('error-message')}>{errorMessage}</p>}
    </div>
  );
};

export default DateTimePicker;

